import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { FileText, Briefcase, Mail, AlertTriangle, Shield } from 'lucide-react';
import { aboutAPI, portfolioAPI, contactAPI } from '../../services/api';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    aboutSections: 0,
    portfolioProjects: 0,
    unreadMessages: 0,
    recentMessages: []
  });
  const [loading, setLoading] = useState(true);
  const [showSecurityWarning, setShowSecurityWarning] = useState(false);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [aboutRes, portfolioRes, messagesRes] = await Promise.all([
          aboutAPI.get(),
          portfolioAPI.getAll(),
          contactAPI.getAll()
        ]);

        const messages = messagesRes.data || [];
        const unreadCount = messages.filter(m => m.status === 'unread').length;

        setStats({
          aboutSections: aboutRes.data?.sections?.length || 0,
          portfolioProjects: portfolioRes.data?.length || 0,
          unreadMessages: unreadCount,
          recentMessages: messages.slice(0, 3)
        });

        // Check if this might be first login (show security warning)
        const hasChangedPassword = localStorage.getItem('passwordChanged');
        if (!hasChangedPassword) {
          setShowSecurityWarning(true);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const dismissSecurityWarning = () => {
    setShowSecurityWarning(false);
  };

  const statCards = [
    {
      icon: FileText,
      label: 'About Sections',
      value: stats.aboutSections,
      link: '/admin/about',
      color: 'from-blue-500/20 to-purple-500/20'
    },
    {
      icon: Briefcase,
      label: 'Portfolio Projects',
      value: stats.portfolioProjects,
      link: '/admin/portfolio',
      color: 'from-green-500/20 to-teal-500/20'
    },
    {
      icon: Mail,
      label: 'Unread Messages',
      value: stats.unreadMessages,
      link: '/admin/messages',
      color: 'from-orange-500/20 to-red-500/20',
      badge: stats.unreadMessages > 0 ? 'new' : null
    }
  ];

  return (
    <AdminLayout>
      <div>
        <h1 className="text-4xl font-light text-white mb-2">Dashboard</h1>
        <p className="text-gray-400">Welcome back, {localStorage.getItem('adminUsername') || 'admin'}</p>
      </div>

      {/* Security Warning Banner */}
      {showSecurityWarning && (
        <div className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-yellow-400 font-medium mb-1">Security Recommendation</h3>
              <p className="text-sm text-yellow-400/80 mb-3">
                If you're using the default password, please change it immediately to secure your admin panel.
              </p>
              <div className="flex space-x-3">
                <Link
                  to="/admin/settings"
                  className="inline-flex items-center px-4 py-2 bg-yellow-500/20 border border-yellow-500/40 text-yellow-400 text-sm hover:bg-yellow-500/30 transition-colors"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Change Password
                </Link>
                <button
                  onClick={dismissSecurityWarning}
                  className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="mt-8 text-gray-400">Loading dashboard...</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            {statCards.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <Link
                  key={index}
                  to={stat.link}
                  className="group relative overflow-hidden border border-white/10 p-6 transition-all duration-300 hover:border-white/30 hover:bg-white/5"
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 transition-opacity duration-300 group-hover:opacity-100`} />
                  <div className="relative">
                    <div className="flex items-start justify-between mb-4">
                      <Icon className="w-8 h-8 text-gray-400 transition-colors duration-300 group-hover:text-white" />
                      {stat.badge && (
                        <span className="text-xs px-2 py-1 bg-red-500/20 text-red-400 border border-red-500/30">
                          {stat.badge}
                        </span>
                      )}
                    </div>
                    <div className="text-4xl font-light text-white mb-2">{stat.value}</div>
                    <div className="text-sm text-gray-400">{stat.label}</div>
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="border border-white/10 p-6">
              <h2 className="text-xl font-light text-white mb-4">Recent Messages</h2>
              {stats.recentMessages.length > 0 ? (
                <div className="space-y-3">
                  {stats.recentMessages.map((message) => (
                    <div key={message.id} className="border-l border-white/20 pl-4 transition-colors duration-300 hover:border-white/60">
                      <div className="flex items-start justify-between mb-1">
                        <div className="font-medium text-white">{message.name}</div>
                        <span className={`text-xs px-2 py-1 ${
                          message.status === 'unread' 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-gray-500/20 text-gray-400'
                        }`}>
                          {message.status}
                        </span>
                      </div>
                      <div className="text-sm text-gray-400 line-clamp-2">{message.message}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No messages yet</p>
              )}
              <Link
                to="/admin/messages"
                className="mt-4 inline-block text-sm text-gray-400 hover:text-white transition-colors duration-300"
              >
                View all messages →
              </Link>
            </div>

            <div className="border border-white/10 p-6">
              <h2 className="text-xl font-light text-white mb-4">Quick Actions</h2>
              <div className="space-y-2">
                <Link
                  to="/admin/about"
                  className="block w-full text-left px-4 py-3 border border-white/10 text-gray-300 hover:border-white/30 hover:text-white transition-all duration-300"
                >
                  Edit About Content
                </Link>
                <Link
                  to="/admin/portfolio"
                  className="block w-full text-left px-4 py-3 border border-white/10 text-gray-300 hover:border-white/30 hover:text-white transition-all duration-300"
                >
                  Add New Project
                </Link>
                <Link
                  to="/admin/messages"
                  className="block w-full text-left px-4 py-3 border border-white/10 text-gray-300 hover:border-white/30 hover:text-white transition-all duration-300"
                >
                  Check Messages
                </Link>
                <Link
                  to="/admin/settings"
                  className="block w-full text-left px-4 py-3 border border-white/10 text-gray-300 hover:border-white/30 hover:text-white transition-all duration-300"
                >
                  <span className="flex items-center">
                    <Shield className="w-4 h-4 mr-2" />
                    Security Settings
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  );
};

export default AdminDashboard;
